/**
 * Backward compatibility type aliases and transformers
 *
 * This file provides backward-compatible types that bridge the gap between
 * the OpenAPI-generated types and legacy consumer expectations.
 */

import type { BackendApiComponents as components } from '@loci/types';

/**
 * Distance band thresholds for categorizing event distances
 */
const DISTANCE_BANDS = [
  { threshold: 1, label: '< 1km' },
  { threshold: 5, label: '1-5km' },
  { threshold: 10, label: '5-10km' },
  { threshold: 25, label: '10-25km' },
] as const;

/**
 * Legacy NearbyEvent type with backward-compatible properties
 *
 * Extends the OpenAPI-generated NearbyEvent with:
 * - distance: Alias for distanceKm (for backward compatibility)
 * - distanceBand: Computed distance range label
 * - category: First category from categories array (for backward compatibility)
 */
export type LegacyNearbyEvent = Omit<components['schemas']['NearbyEvent'], 'location'> & {
  /**
   * Distance in kilometers (alias for distanceKm)
   * @deprecated Use distanceKm or distanceMiles from OpenAPI spec
   */
  distance?: number;

  /**
   * Human-readable distance band (e.g., "< 1km", "1-5km", "5-10km", "> 10km")
   * @deprecated Compute from distanceKm as needed
   */
  distanceBand?: string;

  /**
   * Primary category slug (first from categories array)
   * @deprecated Use categories array from OpenAPI spec
   */
  category: string;

  /**
   * Location name as string (extracted from location.name)
   * @deprecated Use location object from OpenAPI spec
   */
  location: string;
};

/**
 * Transform OpenAPI NearbyEvent to legacy format
 *
 * This helper adds backward-compatible properties to match consumer expectations:
 * - distance: Populated from distanceKm
 * - distanceBand: Computed distance range label
 * - category: First category slug from categories array
 */
export function toLegacyNearbyEvent(
  event: components['schemas']['NearbyEvent'],
): LegacyNearbyEvent {
  const { location: _location, ...eventWithoutLocation } = event;

  const legacy: LegacyNearbyEvent = {
    ...eventWithoutLocation,
    category: 'uncategorized',
    location: _location?.name ?? 'Unknown Location',
  };

  // Add backward-compatible distance property
  if (
    event.distanceKm !== undefined &&
    event.distanceKm !== null &&
    typeof event.distanceKm === 'number'
  ) {
    legacy.distance = event.distanceKm;

    // Compute distance band
    const km = event.distanceKm;
    const band = DISTANCE_BANDS.find((b) => km < b.threshold);
    legacy.distanceBand = band ? band.label : '> 25km';
  }

  // Add backward-compatible category property
  if (event.categories && Array.isArray(event.categories) && event.categories.length > 0) {
    // Extract slug from first category object
    const firstCategory = event.categories[0];
    // Type guard for category object
    if (
      typeof firstCategory === 'object' &&
      firstCategory !== null &&
      'slug' in firstCategory &&
      typeof firstCategory.slug === 'string'
    ) {
      legacy.category = firstCategory.slug;
    }
  }

  return legacy;
}

/**
 * Transform array of OpenAPI NearbyEvents to legacy format
 */
export function toLegacyNearbyEvents(
  events: components['schemas']['NearbyEvent'][],
): LegacyNearbyEvent[] {
  return events.map(toLegacyNearbyEvent);
}
